if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.handleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.preorderMessage = document.querySelector('.product-form__preorder-message');
        this.preorderProperties = this.querySelector('[data-preorder-properties]');
        this.preorderConfig = {
          buttonLabel: this.dataset.preorderButtonLabel || 'Pre-order',
          messageDefault: this.dataset.preorderMessageDefault || '',
          showMessage: this.dataset.preorderShowMessage === 'true',
        };
        this.preorderVariants = this.getPreorderVariants();

        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        this.updatePreorderState();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
      }

      getPreorderVariants() {
        const root = this.closest('product-info') || this;
        const script = root.querySelector('[data-preorder-variants]');
        if (!script) return new Map();

        try {
          const variants = JSON.parse(script.textContent);
          return new Map(
            (variants || []).map((variant) => [String(variant.id), variant])
          );
        } catch (error) {
          console.error('Error parsing preorder variants JSON:', error);
          return new Map();
        }
      }

      handleVariantChange({ data } = {}) {
        if (!data?.sectionId || data.sectionId !== this.dataset.sectionId) return;
        this.updatePreorderState(data.variant);
      }

      updatePreorderState(variantData = {}) {
        const variantId = this.variantIdInput?.value;
        if (!variantId) {
          this.clearPreorderState();
          return;
        }

        const preorderVariant = this.preorderVariants.get(String(variantId)) || {};
        if (Object.keys(preorderVariant).length === 0) {
          return;
        }

        const inventoryQuantity = Number(
          preorderVariant.inventory_quantity ?? variantData.inventory_quantity
        );
        const inventoryPolicy = preorderVariant.inventory_policy ?? variantData.inventory_policy;
        const preorderEnabled = preorderVariant.preorder_enabled === true;

        const isOutOfStock = Number.isFinite(inventoryQuantity) && inventoryQuantity <= 0;
        const isContinueSelling = inventoryPolicy === 'continue';
        const isPreorderEligible = isOutOfStock && isContinueSelling && preorderEnabled;

        if (!isPreorderEligible) {
          this.clearPreorderState();
          return;
        }

        const resolvedMessage = this.resolvePreorderMessage(preorderVariant);
        this.applyPreorderUI(resolvedMessage);
      }

      resolvePreorderMessage(preorderVariant = {}) {
        const variantMessage = (preorderVariant.preorder_message || '').trim();
        if (variantMessage) return variantMessage;
        return (this.preorderConfig?.messageDefault || '').trim();
      }

      applyPreorderUI(message) {
        if (this.submitButton) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        }
        if (this.submitButtonText && this.preorderConfig?.buttonLabel) {
          this.submitButtonText.textContent = this.preorderConfig.buttonLabel;
        }

        if (this.preorderMessage) {
          const shouldShowMessage = this.preorderConfig?.showMessage && message;
          this.preorderMessage.textContent = message;
          this.preorderMessage.toggleAttribute('hidden', !shouldShowMessage);
        }

        this.setPreorderProperties(message);
      }

      clearPreorderState() {
        if (this.preorderMessage) {
          this.preorderMessage.textContent = '';
          this.preorderMessage.setAttribute('hidden', 'hidden');
        }
        if (this.preorderProperties) {
          this.preorderProperties.innerHTML = '';
        }
      }

      setPreorderProperties(message) {
        if (!this.preorderProperties) return;
        this.preorderProperties.innerHTML = '';

        const preorderInput = document.createElement('input');
        preorderInput.type = 'hidden';
        preorderInput.name = 'properties[Pre-order]';
        preorderInput.value = 'Yes';

        const shipsInInput = document.createElement('input');
        shipsInInput.type = 'hidden';
        shipsInInput.name = 'properties[Ships in]';
        shipsInInput.value = message;

        this.preorderProperties.append(preorderInput, shipsInInput);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        // Null-safe: custom PDPs may not include a .loading__spinner element.
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (this.cart) this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (this.cart) {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            // Null-safe spinner cleanup.
            const spinnerEnd = this.querySelector('.loading__spinner');
            if (spinnerEnd) spinnerEnd.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
